import { Input } from '@mm/components';
import {
  HubspotAccessToken,
  tokenErrorMsg,
} from '@mm/state/model/ConnectionModel';
import classNames from 'classnames';
import React, { MouseEvent, useEffect, useState } from 'react';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { HelpCenter } from '../../../../../../../../common/components/HelpCenter/HelpCenter';
import { KlaviyoErrorMessage } from '../KlaviyoIntegrations/KlaviyoErrorMessage';
import styles from './HubspotIntegrations.module.css';

type Props = {
  token: HubspotAccessToken | undefined;
  helpLink: string;
  createApiKey: (privateKey: string) => void;
  updateApiKey: (account: HubspotAccessToken, privateKey: string) => void;
  hasNativeIntegrationsFeature: boolean | undefined;
  showUpgradeModal: (onSuccessfulBilling?: () => void) => void;
  controlsDisabled: boolean;
};

export const HubspotApiKeyConnection = (props: Props) => {
  const {
    token,
    helpLink,
    hasNativeIntegrationsFeature,
    createApiKey,
    updateApiKey,
    controlsDisabled,
    showUpgradeModal,
  } = props;
  const privateKey = token?.accessToken;
  const [apiKey, setApiKey] = useState(token?.accessToken);
  const [apiKeyError, setApiKeyError] = useState('');

  const disabledApiKey = !!(privateKey && apiKey === privateKey);

  useEffect(() => {
    if (token?.status === 'error') {
      setApiKeyError(tokenErrorMsg('apiKey', token.errorInfo));
    }
  }, [token]);

  useEffect(() => {
    setApiKey(privateKey);
  }, [privateKey, setApiKey]);

  const connectApiKey = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    if (!apiKey || apiKey.length < 40) {
      setApiKeyError('Invalid API Key');
      return;
    }

    if (!hasNativeIntegrationsFeature) {
      showUpgradeModal(connectApiKey);
    } else {
      if (token) {
        updateApiKey(token, apiKey);
      } else {
        createApiKey(apiKey);
      }
      setApiKeyError('');
    }
  };

  return (
    <div
      className={classNames(styles.box, {
        [styles.boxDisabled]: controlsDisabled,
      })}
    >
      <div className={styles.boxWithHelp}>
        <div>
          <div className={styles.heading}>Access Token</div>
          <div className={styles.apiKeyInput}>
            <Input
              placeholder="Enter your Access Token"
              css={{ container: styles.inputContainer, input: styles.input }}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
            />
            <PrimaryButton
              className={styles.primaryButton}
              onClick={e => connectApiKey(e)}
              disabled={disabledApiKey}
            >
              {token && disabledApiKey
                ? 'Connected'
                : token
                ? 'Update'
                : 'Connect'}
            </PrimaryButton>
          </div>
        </div>
        <HelpCenter helpLink={helpLink} className={styles.help} />
      </div>
      {apiKeyError && (
        <KlaviyoErrorMessage
          classNames={classNames(
            styles.syncWarningBox,
            styles.apiKeyWarningBox,
          )}
          error={apiKeyError}
        />
      )}
    </div>
  );
};
